import {
  connectSessionResponseSchema,
  connectionListResponseSchema,
  connectionResponseSchema,
  connectionSyncResponseSchema,
  type ConnectionListResponse,
  type CreateConnectionRequest,
} from '@coworker/contracts';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider.js';
import { ApiError } from '../lib/api-error.js';
import { useCurrentWorkspace } from './use-current-workspace.js';
import { useRecoverSession } from './use-recover-session.js';

/**
 * The workspace's connections to somebody else's SaaS - WEB-010, INT-006.
 *
 * Keyed by workspace id like `useEmployees`, so switching workspaces is a
 * different cache entry rather than a stale list under a new heading.
 */
export const connectionsQueryKey = (workspaceId: string | undefined) =>
  ['connections', workspaceId] as const;

export interface ConnectionsState {
  readonly query: UseQueryResult<ConnectionListResponse, unknown>;
  readonly isUnauthenticated: boolean;
}

export function useConnections(): ConnectionsState {
  const { api, status } = useAuth();
  const workspace = useCurrentWorkspace();

  const query = useQuery({
    queryKey: connectionsQueryKey(workspace?.id),
    // Gated on the workspace for the reason `useEmployees` gives: without an id
    // the request goes out with no `x-workspace-id` and comes back 400, which
    // reads as a failure when the truth is there is nothing to ask about yet.
    enabled: status === 'signedIn' && workspace !== undefined,
    queryFn: () =>
      api.request('/v1/connections', {
        schema: connectionListResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
  });

  const isUnauthenticated = query.error instanceof ApiError && query.error.isUnauthenticated;
  useRecoverSession({ isUnauthenticated, erroredAt: query.errorUpdatedAt });

  return { query, isUnauthenticated };
}

/**
 * Opens a connection, before anybody has been to the provider - INT-006.
 *
 * Two steps and not one, because the row has to exist before a connect token
 * can be scoped to it: `POST /v1/connections/{id}/session` needs an id. That is
 * what lets two HubSpot connections in one workspace stay distinguishable,
 * which INT-008 requires.
 */
export function useCreateConnection() {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateConnectionRequest) =>
      api.request('/v1/connections', {
        method: 'POST',
        schema: connectionResponseSchema,
        workspaceId: workspace?.id ?? '',
        body: input,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: connectionsQueryKey(workspace?.id) }),
  });
}

/** A short-lived token and the URL the browser opens - INT-006. */
export function useConnectSession() {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();

  return useMutation({
    mutationFn: (connectionId: string) =>
      api.request(`/v1/connections/${connectionId}/session`, {
        method: 'POST',
        schema: connectSessionResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
  });
}

/**
 * Asks the server whether the connection completed - INT-006.
 *
 * **A poll, because there is no webhook.** No ticket owns one - ADR-0040
 * records that - so the screen asks once the person says they are done. Framing
 * it as their action rather than a background timer is deliberate: a spinner
 * that polls forever cannot tell "still connecting" from "gave up", and a
 * button can.
 */
export function useSyncConnection() {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) =>
      api.request(`/v1/connections/${connectionId}/sync`, {
        method: 'POST',
        schema: connectionSyncResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: connectionsQueryKey(workspace?.id) }),
  });
}

/**
 * Ends a connection here and at the provider - INT-007.
 *
 * The API disconnects at Pipedream first and fails the request if it cannot, so
 * an error here means the connection is **still live** - which is why the screen
 * shows the message rather than optimistically removing the row.
 */
export function useDisconnectConnection() {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) =>
      api.request(`/v1/connections/${connectionId}`, {
        method: 'DELETE',
        schema: connectionResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: connectionsQueryKey(workspace?.id) }),
  });
}
